import React from 'react';
import { Box, Text, Image, Flex } from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { Campaign, Collab, Client } from '@foodfluence/shared';
import type { CardCta } from '@foodfluence/shared';
import { ff } from '../../../../theme';

export type RowPillTone = 'green' | 'red' | 'grey' | 'limited';

const PILL_TONES: Record<RowPillTone, { bg: string; color: string }> = {
  green: { bg: '#E4F3CC', color: '#2C5208' },
  red: { bg: '#FBE1DC', color: '#A1260F' },
  grey: { bg: '#F0F0EE', color: '#55555A' },
  limited: { bg: ff.ink, color: ff.lime },
};

const CTA_TONES: Record<CardCta['variant'], RowPillTone> = {
  green: 'green',
  red: 'red',
  gray: 'grey',
  spots: 'limited',
  arrow: 'grey',
};

/** Right-hand status pill on a Home row. Clicks on it don't open the row. */
export const RowPill: React.FC<{ tone: RowPillTone; children: React.ReactNode }> = ({ tone, children }) => (
  <Flex
    as="span"
    flex="none"
    h="28px"
    px="10px"
    borderRadius="full"
    align="center"
    fontSize="12px"
    fontWeight={600}
    whiteSpace="nowrap"
    cursor="default"
    onClick={(e) => e.stopPropagation()}
    {...PILL_TONES[tone]}
  >
    {children}
  </Flex>
);

export const RowArrow = () => (
  <Box as="span" flex="none" color={ff.muted}>
    <FiChevronRight size={18} strokeWidth={2.4} aria-hidden />
  </Box>
);

/** 48px rounded logo, or initials on grey when there's no image. */
export const RowAvatar: React.FC<{ src?: string; name: string }> = ({ src, name }) => {
  const initials = name.replace(/[^A-Za-z ]/g, '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <Flex as="span" flex="none" w="48px" h="48px" borderRadius="12px" overflow="hidden" bg={ff.grey} align="center" justify="center" fontSize="15px" fontWeight={600} color={ff.ink}>
      {src ? <Image src={src} alt={name} w="100%" h="100%" objectFit="contain" /> : initials || '?'}
    </Flex>
  );
};

/** True when the collab or its campaign is UGC (content for the brand, not the creator's feed). */
export const isUgcCollab = (collab?: Pick<Collab, 'creatorType'>, campaign?: Pick<Campaign, 'creatorType'>) =>
  collab?.creatorType === 'ugc' || campaign?.creatorType === 'ugc';

/** Small ink "UGC" tag that sits after a row's first line. */
const UgcTag = () => (
  <Flex as="span" flex="none" h="20px" px="7px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" fontSize="10px" fontWeight={600} letterSpacing="0.1em" lineHeight="normal">
    UGC
  </Flex>
);

/** Board row: avatar, bold first line, secondary line, optional meta, right slot. */
export const HomeRow: React.FC<{
  avatar: React.ReactNode;
  primary: React.ReactNode;
  ugc?: boolean;
  secondary?: React.ReactNode;
  meta?: React.ReactNode;
  urgent?: boolean;
  right: React.ReactNode;
  onClick: () => void;
}> = ({ avatar, primary, ugc = false, secondary, meta, urgent = false, right, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    w="100%"
    align="center"
    gap="14px"
    minH="72px"
    py="12px"
    borderBottom="1px solid"
    borderColor={ff.line}
    color={ff.ink}
    textAlign="left"
  >
    {avatar}
    <Flex as="span" flex={1} minW={0} direction="column" gap="3px">
      <Flex as="span" align="center" gap="8px" minW={0}>
        <Text as="span" fontSize="16px" fontWeight={600} noOfLines={1}>{primary}</Text>
        {ugc && <UgcTag />}
      </Flex>
      {secondary && <Text as="span" fontSize="14px" color={ff.body} noOfLines={1}>{secondary}</Text>}
      {meta && (
        <Text as="span" fontSize="13px" noOfLines={1} fontWeight={urgent ? 700 : 500} color={urgent ? '#A1260F' : ff.text2}>
          {meta}
        </Text>
      )}
    </Flex>
    {right}
  </Flex>
);

interface CollabCardProps {
  collab: Collab;
  campaign?: Campaign;
  client: Client | undefined;
  cta: CardCta;
  timeLeft?: string | null;
  subtitle?: string;
  onClick: () => void;
}

const CollabCard: React.FC<CollabCardProps> = ({ collab, campaign, client, cta, timeLeft, subtitle, onClick }) => {
  const name = client?.accountName ?? 'Unknown Brand';
  return (
    <HomeRow
      onClick={onClick}
      avatar={<RowAvatar name={name} src={client?.instagramProfilePicture || campaign?.collabImage || client?.bannerPicture} />}
      primary={name}
      ugc={isUgcCollab(collab, campaign)}
      secondary={subtitle ?? campaign?.name}
      meta={timeLeft ? `Expires in ${timeLeft}` : undefined}
      urgent
      right={cta.variant === 'arrow' ? <RowArrow /> : <RowPill tone={CTA_TONES[cta.variant]}>{cta.label}</RowPill>}
    />
  );
};

export default CollabCard;
